/**
 * AVBP Leads — the Fiverr second-pipeline discriminator (AD-046, Phase 0).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS
 * ─────────────────────────────────────────────────────────────────────────
 *  Source is a TAG, the pipeline is the state machine. Four of the five
 *  owner-named sources (Referral · Facebook · Google · Website) share the
 *  ONE Standard pipeline — the live `LeadState` enum in `lib/leads.ts`.
 *  Fiverr is the single exception: it runs its own order-shaped pipeline
 *  (Inquiry → Custom Offer → Order Placed → In Delivery → Delivered →
 *  Review). This module is the PURE discriminator + the Fiverr stage enum +
 *  its forward-only guard. It is unit-tested directly by the Node runner
 *  (no React, no Firebase — only a TYPE-ONLY import of `Lead`, erased at
 *  compile, so there is no runtime cycle with `leads.ts`).
 *
 *  ADDITIVE-ONLY (snapshot seam): `leadPipeline` + `fstage` ride INSIDE the
 *  `notes_internal` JSON snapshot (exactly like `agreedValue`/`trashed`), so
 *  there is NO firestore.rules change and no new collection. A lead with no
 *  `leadPipeline` is treated as `'standard'`; a lead with no `fstage` is
 *  simply absent from the Fiverr board. Snapshot fields are NOT queryable —
 *  acceptable because every Lens view filters in-memory off the single
 *  `listenLeads` array.
 * ─────────────────────────────────────────────────────────────────────────
 */
import type { Lead } from './leads';

/** Which pipeline a lead is on. Default (absent) → 'standard'. */
export type LeadPipeline = 'standard' | 'fiverr';

/**
 * The Fiverr order pipeline. Only meaningful when
 * `leadPipeline === 'fiverr'`. Forward-only with park/reject off-ramps that
 * REUSE the Standard `LeadState` off-ramps (a Fiverr lead can still be
 * parked/rejected via the shared `LeadState` machine — `fstage` only models
 * the forward order flow).
 */
export type FStage =
  | 'inquiry'
  | 'custom_offer'
  | 'order_placed'
  | 'in_delivery'
  | 'delivered'
  | 'review';

export const ALL_FSTAGES: FStage[] = [
  'inquiry',
  'custom_offer',
  'order_placed',
  'in_delivery',
  'delivered',
  'review',
];

export const FSTAGE_LABEL: Record<FStage, string> = {
  inquiry: 'Inquiry',
  custom_offer: 'Custom offer',
  order_placed: 'Order placed',
  in_delivery: 'In delivery',
  delivered: 'Delivered',
  review: 'Review',
};

/**
 * Forward-only legal transitions for the Fiverr pipeline. Each stage may
 * advance exactly one hop forward (no skip), mirroring the Standard spine's
 * single-hop discipline; `review` is terminal (the order is complete). A
 * Board drag that is not in this list is rejected and snaps back, exactly
 * like the Standard board's `nextStates()` guard.
 */
const FSTAGE_TRANSITIONS: Record<FStage, FStage[]> = {
  inquiry: ['custom_offer'],
  custom_offer: ['order_placed'],
  order_placed: ['in_delivery'],
  in_delivery: ['delivered'],
  delivered: ['review'],
  review: [], // terminal
};

/** The single authority on Fiverr-stage legality (mirrors `nextStates`). */
export function nextFstages(from: FStage): FStage[] {
  return FSTAGE_TRANSITIONS[from] ?? [];
}

export function canFstageTransition(from: FStage, to: FStage): boolean {
  return FSTAGE_TRANSITIONS[from]?.includes(to) ?? false;
}

export function isFstageTerminal(s: FStage): boolean {
  return (FSTAGE_TRANSITIONS[s]?.length ?? 0) === 0;
}

/* ════════════════════ Discriminator (pure, defensive) ════════════════════ */

/**
 * The pipeline a lead is on. A lead with no `leadPipeline` (every legacy
 * lead) reads as `'standard'`. A lead whose SOURCE is fiverr is also treated
 * as fiverr even if the snapshot field was never written (belt + braces, so
 * an imported Fiverr lead surfaces on the Fiverr board without a backfill).
 */
export function pipelineOf(lead: Pick<Lead, 'leadPipeline' | 'source'>): LeadPipeline {
  if (lead.leadPipeline === 'fiverr') return 'fiverr';
  // Source-derived fallback: a lead tagged with the fiverr source string is
  // a fiverr lead. (`source` is the live LeadSource enum; 'fiverr' is a
  // future additive value — string-compared so this is forward-safe today.)
  if (String(lead.source) === 'fiverr') return 'fiverr';
  return 'standard';
}

export function isFiverr(lead: Pick<Lead, 'leadPipeline' | 'source'>): boolean {
  return pipelineOf(lead) === 'fiverr';
}

/** The current Fiverr stage of a lead (defaults to 'inquiry' when absent). */
export function fstageOf(lead: Pick<Lead, 'fstage'>): FStage {
  const s = lead.fstage;
  return s && (ALL_FSTAGES as string[]).includes(s) ? (s as FStage) : 'inquiry';
}

/* ════════════════════ L0 sub-tag (the AD-047 contract) ════════════════════ */

/**
 * The L0 product sub-tag carried in the snapshot — NOT a new top-level
 * source value (that would break the 5-source lens reading). It attributes
 * an L0 lead finer than its source: which free magnet (or the consultation)
 * produced it. `astro_vastu_599` is accepted as a historical alias and
 * normalised to `astro_vastu`.
 */
export type L0Product =
  | 'E2'
  | 'E4'
  | 'general'
  | 'custom'
  | 'astro_vastu';

export const L0_PRODUCT_LABEL: Record<L0Product, string> = {
  E2: 'E2 · Property Score',
  E4: 'E4 · Room-by-Room',
  general: 'General report',
  custom: 'Custom report',
  astro_vastu: 'Astro-Vastu (family)',
};

/** Normalise any raw l0Product string onto the canonical union, else null. */
export function normaliseL0Product(raw: unknown): L0Product | null {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return null;
  if (s === 'e2') return 'E2';
  if (s === 'e4') return 'E4';
  if (s === 'general') return 'general';
  if (s === 'custom') return 'custom';
  if (s === 'astro_vastu' || s === 'astro_vastu_599' || s === 'astro-vastu')
    return 'astro_vastu';
  return null;
}
